import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, FileText, X, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BusinessDocumentationModal = ({
  isOpen,
  onClose,
  businessCompleted,
  businessUploadedFiles = [],
  isBusinessUploading = false,
  businessUploadProgress = '',
  businessUploadStatus = '',
  onBusinessFileUpload,
  onViewBusinessFile,
  onDeleteBusinessFile,
  onCompleteBusinessDocumentation,
  isViewingBusinessFile = false
}) => {
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0 && onBusinessFileUpload) {
      onBusinessFileUpload({ target: { files } });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Business Documentation</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:text-emerald-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6 text-[#1a4a1a]">
                {/* Status Section */}
                <div className={`p-4 rounded-lg ${businessCompleted ? 'border bg-green-50 border-green-200' : 'border-2 bg-sky-50 border-sky-300'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {businessCompleted ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-sky-600" />
                    )}
                    <h3 className={`font-semibold ${businessCompleted ? 'text-green-800' : 'text-sky-900'}`}>
                      Status: {businessCompleted ? 'Completed' : 'Pending'}
                    </h3>
                  </div>
                  <p className={`text-sm ${businessCompleted ? 'text-green-700' : 'text-sky-800'}`}>
                    {businessCompleted 
                      ? 'Your business documentation has been submitted successfully. All documents have been received and are being reviewed by our team.'
                      : 'Please upload your business documents for review. These documents will be reviewed by our team as part of the Shariah certification process.'
                    }
                  </p>
                </div>

                {/* Requirements Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Required Business Documents</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Risk Management</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• Risk assessment framework</li>
                        <li>• Risk management policies</li>
                        <li>• Risk monitoring procedures</li>
                        <li>• Risk mitigation strategies</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Credit Process Summary</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• Credit evaluation process</li>
                        <li>• Credit scoring methodology</li>
                        <li>• Approval workflows</li>
                        <li>• Credit monitoring systems</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Financial Flow</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• Financial transaction flow</li>
                        <li>• Payment processing systems</li>
                        <li>• Revenue recognition</li>
                        <li>• Financial reporting structure</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Operational Model</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• Business operations overview</li>
                        <li>• Service delivery model</li>
                        <li>• Customer onboarding process</li>
                        <li>• Operational workflows</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> All documents are treated with strict confidentiality and will only be used for certification review purposes. 
                      These documents are stored securely with encryption and access controls.
                    </p>
                  </div>
                </div>

                {/* File Upload Section */}
                {!businessCompleted && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Upload Business Documents</h3>
                    <div className="border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center">
                      <div className="text-2xl mb-3">📋</div>
                      <p className="text-sm text-gray-600 mb-4">
                        Upload your confidential business documents for review. 
                        Supported formats: PDF, DOC, DOCX, RTF, XLS, XLSX, ODT, ODS, TXT, CSV, JPG, PNG (Max 10MB each)
                      </p>
                      <p className="text-xs text-emerald-600 mb-4">
                        💡 Documents are stored securely in trusted cloud storage with encryption and access controls.
                      </p>
                      
                      {/* Upload Progress Indicator */}
                      {isBusinessUploading && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <span className="text-sm font-medium text-blue-800">{businessUploadProgress}</span>
                          </div>
                          <p className="text-xs text-blue-600">{businessUploadStatus}</p>
                        </div>
                      )}
                      
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.rtf,.xls,.xlsx,.odt,.ods,.txt,.csv,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        disabled={isBusinessUploading}
                        className="hidden"
                        id="business-file-upload"
                      />
                      <label
                        htmlFor="business-file-upload"
                        className={`inline-flex items-center px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                          isBusinessUploading 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-emerald-600 text-white hover:bg-emerald-700'
                        }`}
                      >
                        {isBusinessUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Uploading to Secure Storage...
                          </>
                        ) : (
                          'Upload Documents'
                        )}
                      </label>
                    </div>
                  </div>
                )}

                {/* Uploaded Files Section */}
                {businessUploadedFiles.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Uploaded Business Documents</h3>
                    <div className="space-y-3">
                      {businessUploadedFiles.map((file) => (
                        <div key={file.id || file.s3Key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="text-2xl">☁️</div>
                            <div>
                              <p className="font-medium text-sm">{file.name}</p>
                              <p className="text-xs text-gray-500">
                                {file.uploadDate ? new Date(file.uploadDate).toLocaleDateString() : 'Date not available'} • 
                                {file.size ? ` ${(file.size / 1024 / 1024).toFixed(2)} MB` : ' Size not available'}
                                <span className="ml-2 text-emerald-600">• Secure Storage</span>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onViewBusinessFile && onViewBusinessFile(file)}
                              disabled={isViewingBusinessFile}
                              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isViewingBusinessFile ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2"></div>
                                  Loading...
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </>
                              )}
                            </Button>
                            {!businessCompleted && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onDeleteBusinessFile && onDeleteBusinessFile(file.id || file.s3Key)}
                                className="border-red-600 text-red-600 hover:bg-red-50"
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completion Section */}
                {!businessCompleted && businessUploadedFiles.length > 0 && (
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-2">Ready to Submit</h4>
                    <p className="text-sm text-emerald-700 mb-4">
                      You have uploaded {businessUploadedFiles.length} business document(s). Review your files and click 
                      "Complete Submission" when ready to submit for review. This will proceed to the Shariah certification step.
                    </p>
                    <Button
                      onClick={onCompleteBusinessDocumentation}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Complete Submission
                    </Button>
                  </div>
                )}

                {/* Completed Status */}
                {businessCompleted && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">Business Documentation Submitted</h4>
                    </div>
                    <p className="text-sm text-green-700 mb-3">
                      Your business documentation has been successfully submitted. 
                      The documents are under review and you can proceed to the Shariah certification step.
                    </p>
                    <div className="space-y-2 text-sm text-green-700">
                      <p>✅ Business documents received</p>
                      <p>✅ Risk management documentation reviewed</p>
                      <p>✅ Credit process documentation received</p>
                      <p>✅ Financial flow documentation received</p>
                      <p>✅ Operational model documentation received</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {businessCompleted 
                    ? 'Business documentation submitted successfully'
                    : `${businessUploadedFiles.length} business document(s) uploaded`
                  }
                </p>
                <Button
                  onClick={onClose}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BusinessDocumentationModal;

